import {
  Button,
  Input,
  InputWrapperProps,
  Modal,
  NumberInputProps,
  Skeleton,
  Tooltip,
} from "@mantine/core";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Promises, useFormHelperContext } from "../form";
import { FieldError, get, useFieldArray } from "react-hook-form";
import { InputWrapper } from "../inputWrapper";
import { useTranslation } from "react-i18next";
import { IconDownload, IconTrash } from "@tabler/icons-react";
import { createNotification } from "utils/notifications/notifications";
import {
  ACCEPTED_EXTENSIONS_COMPRESSED_FILE_COMPONENT,
  ACCEPTED_EXTENSIONS_PREVIEW_FILE_DOCUMENT_COMPONENT,
  ACCEPTED_EXTENSIONS_PREVIEW_FILE_IMAGE_COMPONENT,
  IframeComponentPreviewDocs,
  IframeComponentPreviewImage,
  IframeComponentPreviewPDF,
  getBaseFilename,
  sanitizeUrl,
} from "@components/@general/preview-doc";
import { useDisclosure } from "@mantine/hooks";
import useAuthStore from "@store/authstore";
import { useFetchFileUrl } from "utils/hooks/pizza";
import { decryptUrl } from "utils/koceng";

export interface ResponseResolver {
  filename: string;
  url: string;
}

export interface InputFileRef {
  reset: () => void;
}
export interface BaseInputFileProps extends NumberInputProps {
  label: string;
  name: string;
  multiple?: boolean;
  inputWrapper?: InputWrapperProps;
  multiField?: boolean;
  ref?: React.RefObject<InputFileRef | null>;
  accept?: string;
  maxFileSize?: number;
  disableDeleteBtn?: boolean;
  disablePreviewButton?: boolean;
}

export interface FieldInputFileProps extends BaseInputFileProps {
  resolver: (file: File) => ResponseResolver | Promise<ResponseResolver>;
}

export interface FieldInputMultiFileProps extends BaseInputFileProps {
  resolver: (
    file: File | File[]
  ) =>
    | ResponseResolver
    | ResponseResolver[]
    | Promise<ResponseResolver>
    | Promise<ResponseResolver[]>;
}

const CURRENT_DOMAIN =
  typeof window !== "undefined" ? window.location.origin : "";
interface PreviewFileProps {
  url: string;
  filename?: string;
  extFile?: string;
}

export const PreviewFileUploaded = ({
  url,
  filename,
  extFile: propExtFile,
}: PreviewFileProps) => {
  const cleanUrl = sanitizeUrl(url);
  const cleanName = getBaseFilename(filename);

  const { fetchUrl, extFile: fetchedExt, isLoading } = useFetchFileUrl({
    filename: cleanName,
    url: cleanUrl,
  });

  const ext = (
    propExtFile ||
    fetchedExt ||
    (cleanName.includes(".") ? cleanName.split(".").pop() : "") ||
    ""
  ).toLowerCase();

  if (!cleanUrl || isLoading) {
    return <Skeleton visible={true} height={300} width="100%" />;
  }

  if (ACCEPTED_EXTENSIONS_PREVIEW_FILE_IMAGE_COMPONENT.includes(ext)) {
    return <IframeComponentPreviewImage url={cleanUrl} filename={cleanName} />;
  }

  if (ACCEPTED_EXTENSIONS_PREVIEW_FILE_DOCUMENT_COMPONENT.includes(ext)) {
    return <IframeComponentPreviewDocs url={cleanUrl} filename={cleanName} />;
  }

  if (ext === "pdf") {
    return <IframeComponentPreviewPDF url={cleanUrl} filename={cleanName} />;
  }

  if (ACCEPTED_EXTENSIONS_COMPRESSED_FILE_COMPONENT.includes(ext)) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4 text-center">
        <p className="text-gray-600 font-medium">
          Preview tidak tersedia untuk file terkompresi/arsip ({ext.toUpperCase()}).
        </p>
        <Button
          variant="outline"
          color="blue"
          onClick={() => window.open(fetchUrl || cleanUrl, "_blank")}
        >
          Unduh File
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-4 text-center">
      <p className="text-gray-600 font-medium">
        Preview tidak tersedia untuk format file ini ({ext ? `.${ext}` : "tidak diketahui"}).
      </p>
      <Button
        variant="outline"
        color="blue"
        onClick={() => window.open(fetchUrl || cleanUrl, "_blank")}
      >
        Unduh File
      </Button>
    </div>
  );
};

export const PreviewFileUnUploaded = ({
  url,
  filename,
  extFile,
}: PreviewFileProps) => {
  const cleanUrl = sanitizeUrl(url);
  const cleanName = getBaseFilename(filename);
  const ext = extFile
    ? getBaseFilename(extFile).toLowerCase()
    : (cleanName.includes(".") ? cleanName.split(".").pop()?.toLowerCase() || "" : "");

  if (!cleanUrl) {
    return <Skeleton visible={true} height={300} width="100%" />;
  }
  if (ACCEPTED_EXTENSIONS_PREVIEW_FILE_IMAGE_COMPONENT.includes(ext)) {
    return (
      <div
        style={{ height: "auto" }}
        className="flex justify-center items-center"
      >
        <img
          src={cleanUrl}
          alt={cleanName}
          className="max-h-[600px] max-w-full object-contain"
        />
      </div>
    );
  }
  if (ACCEPTED_EXTENSIONS_PREVIEW_FILE_DOCUMENT_COMPONENT.includes(ext)) {
    return <IframeComponentPreviewDocs url={cleanUrl} filename={cleanName} />;
  }
  if (ext === "pdf") {
    return (
      <iframe
        src={`${cleanUrl}`}
        width="100%"
        height="600px"
        style={{ border: "none" }}
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-4 text-center">
      <p className="text-gray-600 font-medium">
        Preview tidak tersedia untuk format file ini ({ext ? `.${ext}` : "tidak diketahui"}).
      </p>
      <Button
        variant="outline"
        color="blue"
        onClick={() => window.open(cleanUrl, "_blank")}
      >
        Unduh File
      </Button>
    </div>
  );
};

const pickErrMsg = (errors: any, path: string): string | undefined => {
  const val = get(errors, path) as unknown;
  if (!val) return undefined;
  if (typeof val === "string") return val;
  if (typeof (val as FieldError)?.message === "string")
    return (val as FieldError).message;
  return undefined;
};

export const FieldInputFile: React.FC<FieldInputFileProps> = ({
  label,
  inputWrapper,
  name,
  resolver,
  accept,
  multiple = false,
  readOnly,
  ref,
  maxFileSize,
  disableDeleteBtn,
  disablePreviewButton = false,
  ...rest
}) => {
  const {
    control,
    formState,
    setValue,
    watch,
    PromisesRef,
    getValues,
    inputRefs,
  } = useFormHelperContext();
  const inputRef = useRef<HTMLInputElement | undefined>(undefined);
  const { t } = useTranslation("generalMessage");
  const errors = formState.errors;
  const [opened, { open, close }] = useDisclosure(false);
  const [errorFileName, setErrorFileName] = useState<string | null>(null);
  const [encryptedUrl, setEncryptedUrl] = useState<string | null>(null);

  // const [fileValue, setFileValue ]= useState<{url:string, filename:string} | null>(null)


  const currentFileValue = getValues(name)

  useEffect(() => {

    const decryptUrlCallback = async () => {
      try {

        if (currentFileValue?.url.length > 0) {
          let urlDecrypt = await decryptUrl(currentFileValue.url)
          if (urlDecrypt.startsWith("http")) {
            setEncryptedUrl(currentFileValue.url)
          }
          setValue(name, { ...currentFileValue, url: urlDecrypt.split("?se=")[0] })
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    decryptUrlCallback()

  }, [])


  const msgError =
    pickErrMsg(errors, `${name}.filename`) ?? pickErrMsg(errors, `${name}`);

  const resetInput = () => {
    if (inputRef.current) {
      setValue(name, null, { shouldDirty: true, shouldValidate: true });
      inputRef.current.value = "";
    }
    if (PromisesRef.current) {
      PromisesRef.current = PromisesRef.current.filter(
        (fn) => (fn as any).fieldName !== name
      );
    }
  };

  useImperativeHandle(ref, () => ({
    reset: resetInput,
  }));

  const handleFileChange = async (file: File | undefined) => {
    if (!file) return;

    // 🔒 Validate filename terlebih dulu
    if (isUnsafeFilename(file.name)) {
      setErrorFileName(
        "Invalid filename (contains invalid characters or has an invalid format)"
      );
      createNotification({
        type: "error",
        title: "Invalid filename",
        message:
          "The filename contains invalid characters or has an invalid format. Please avoid ? # & = % ; / \\ : * \" < > | +, control characters, trailing spaces/dots, or '..'.",
      });
      return;
    } else {
      setErrorFileName(null);
    }

    const acceptExtensions = accept
      ?.split(",")
      .map((ext) => ext.trim().toLowerCase()); // Bersihkan spasi
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase(); // ambil ekstensi dari nama file
    if (acceptExtensions && !acceptExtensions.includes(fileExtension)) {
      createNotification({
        type: "error",
        message: `Invalid file type. Only ${accept} files are accepted.`,
        title: "Error",
      });
      return;
    }

    if (maxFileSize && file.size > maxFileSize) {
      createNotification({
        type: "error",
        message: `File size exceeds the maximum limit of ${maxFileSize / 1024 / 1024
          } MB.`,
        title: "Error",
      });
      return;
    }

    try {
      if (resolver.constructor.name == "AsyncFunction") {
        const resolveFn = () => {
          return (resolver(file) as Promise<ResponseResolver>)
            .then((response) => {
              if (response?.url && response?.filename) {
                setValue(
                  name,
                  { ...response },
                  { shouldDirty: true, shouldValidate: true }
                );
              }
            })
            .catch((err) => {
              console.log(`Failed to send file: ${err}`);
            });
        };
        (resolveFn as any).fieldName = name;
        const prevRef = PromisesRef!.current as unknown as Promises[];
        const updatedPromises: Promises[] = [
          ...prevRef,
          resolveFn as unknown as Promises,
        ];
        PromisesRef.current = updatedPromises;
        // prevRef.push(resolveFn as unknown as Promises)
        // console.log("PromisesRef.current", PromisesRef.current)
      } else if (typeof resolver === "function") {
        const returnedResolver = resolver(file);
        setValue(
          name,
          { ...returnedResolver },
          { shouldDirty: true, shouldValidate: true }
        );
      }

      const url = URL.createObjectURL(file);
      setValue(
        name,
        { url: url, filename: file.name },
        { shouldDirty: true, shouldValidate: true }
      );
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const fileValue = watch(name)


  const cleanFileValueName = fileValue?.filename ? getBaseFilename(fileValue.filename) : "";
  const cleanFileValueUrl = fileValue?.url ? sanitizeUrl(fileValue.url) : "";

  const { fetchUrl, extFile, isLoading, error } = useFetchFileUrl({
    filename: fileValue?.filename || "",
    url: fileValue?.url || "",
  });


  return (
    <InputWrapper
      label={label}
      {...inputWrapper}
      withAsterisk={rest.withAsterisk}
    >
      <div className="flex flex-col w-full">
        <div className="flex flex-grow w-full mb-2">
          <input
            type="file"
            accept={accept}
            data-testid={"field-file-" + name}
            onChange={(e) => handleFileChange(e.target.files?.[0])}
            ref={(current) => {
              if (current) {
                inputRef.current = current;
                inputRefs.current?.push(current!);
              }
            }}
            hidden
          />

          {fileValue?.filename ? (
            <>
              {!disablePreviewButton ? (
                <Button
                  variant="fill"
                  onClick={open}
                  color="blue"
                  className="w-full"
                  data-testid={"field-file-uploaded-" + name}
                >
                  {cleanFileValueName || cleanFileValueUrl}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  color="blue"
                  className="w-full"
                  data-testid={"field-file-uploaded-" + name}
                  disabled
                >
                  {cleanFileValueName || cleanFileValueUrl}
                </Button>
              )}
              {fileValue?.url && (
                <Tooltip label={t("download", { ns: "generalMessage" })}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!fileValue?.url)
                        return console.log("File URL is not available");
                      if (!fileValue?.url.startsWith("blob:")) {
                        return window.open(fetchUrl || "");
                      }
                      return window.open(fileValue?.url);
                    }}
                    color="blue"
                    className="ml-2"
                    data-testid={"field-file-delete-" + name}
                  >
                    <IconDownload size={15} />
                  </Button>
                </Tooltip>
              )}
            </>
          ) : (
            <Button
              variant="outline"
              type="button"
              onClick={() => inputRef.current?.click()}
              color="blue"
              className="w-full"
              data-testid={"field-file-upload-" + name}
              disabled={readOnly}
            >
              {t("clickHereToUpload")}
            </Button>
          )}
          {!disableDeleteBtn && (
            <Tooltip label={t("delete", { ns: "generalMessage" })}>
              <Button
                variant="outline"
                onClick={() => resetInput()}
                color="red"
                className="ml-2"
                data-testid={"field-file-delete-" + name}
                disabled={readOnly}
              >
                <IconTrash size={15} />
              </Button>
            </Tooltip>
          )}
        </div>
        {errorFileName && <Input.Error>{errorFileName}</Input.Error>}
        {msgError && <Input.Error>{msgError}</Input.Error>}
      </div>
      <Modal
        opened={opened}
        onClose={close}
        radius={0}
        transitionProps={{ transition: "fade", duration: 200 }}
        size={"xl"}
        title={fileValue?.filename || fileValue?.url || "Preview File"}
      >
        {fileValue?.filename &&
          fileValue?.url &&
          fileValue?.url.startsWith("blob:") ? (
          <>
            <PreviewFileUnUploaded
              url={fileValue?.url}
              filename={fileValue?.filename}
              extFile={extFile}
            />
          </>
        ) : (
          <>
            <PreviewFileUploaded
              url={encryptedUrl || fileValue?.url || ""}
              filename={fileValue?.filename || ""}
              extFile={extFile}
            />
          </>
        )}
      </Modal>
    </InputWrapper>
  );
};

export const MultiFieldFileInput: React.FC<FieldInputMultiFileProps> = ({
  label,
  name,
  placeholder,
  resolver,
  inputWrapper,
  readOnly,
  accept,
  maxFileSize,
  ...rest
}) => {
  const { register, control, formState, setValue, watch } =
    useFormHelperContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<PreviewFileProps | null>(null);
  if (!register) {
    throw new Error(
      "Field.MultiText must be used within a FormHelper component"
    );
  }
  if (!resolver) {
    throw new Error("Resolver must be provided");
  }

  const { t } = useTranslation("generalMessage");
  const errors = formState.errors;
  const [errorFileName, setErrorFileName] = useState<string | null>(null);

  const handleFileChange = async (file: File | undefined, index: number) => {
    if (!file) return;

    // 🔒 Validate filename terlebih dulu
    if (isUnsafeFilename(file.name)) {
      setErrorFileName(
        "Invalid filename (contains invalid characters or has an invalid format)"
      );
      createNotification({
        type: "error",
        title: "Invalid filename",
        message:
          "The filename contains invalid characters or has an invalid format. Please avoid ? # & = % ; / \\ : * \" < > | +, control characters, trailing spaces/dots, or '..'.",
      });
      return;
    } else {
      setErrorFileName(null);
    }

    const acceptExtensions = accept
      ?.split(",")
      .map((ext) => ext.trim().toLowerCase()); // Bersihkan spasi
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase(); // ambil ekstensi dari nama file
    if (acceptExtensions && !acceptExtensions.includes(fileExtension)) {
      createNotification({
        type: "error",
        message: `Invalid file type. Only ${accept} files are accepted.`,
        title: "Error",
      });
      return;
    }

    if (maxFileSize && file.size > maxFileSize) {
      createNotification({
        type: "error",
        message: `File size exceeds the maximum limit of ${maxFileSize / 1024 / 1024
          } MB.`,
        title: "Error",
      });
      return;
    }

    try {
      const response = await resolver(file);
      //    if single file
      if (
        response &&
        !Array.isArray(response) &&
        typeof response.url === "string" &&
        typeof response.filename === "string"
      ) {
        setValue(`${name}.${index}`, response);
      }
      //  if multiple files
      if (response && Array.isArray(response)) {
        setValue(`${name}.${index}`, response);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const inputRef = useRef<HTMLInputElement[] | undefined[] | []>([]);

  const handleSelectAttachment = (attachment: PreviewFileProps) => {
    setSelectedAttachment(attachment);
    open();
  };

  // Manage extFile state for each field index outside the map
  const [extFiles, setExtFiles] = useState<string[]>([]);

  useEffect(() => {
    // For each field, determine the extension and update extFiles state
    const updateExtFiles = async () => {
      const newExtFiles: string[] = [];
      for (let index = 0; index < fields.length; index++) {
        const url = watch(`${name}.${index}.url`);
        const filename = watch(`${name}.${index}.filename`);
        const cleanName = filename ? getBaseFilename(filename) : "";
        const cleanUrl = url ? sanitizeUrl(url) : "";
        if (cleanName && !cleanName.includes(".")) {
          try {
            const token = useAuthStore.getState().token || "";
            const fingerprint = useAuthStore.getState().fingerprint || "";
            const proxyUrl = `/api/get-meta-data-file?url=${encodeURIComponent(
              cleanUrl
            )}&token=${encodeURIComponent(token)}&fingerprint=${encodeURIComponent(fingerprint)}`;
            const response = await fetch(proxyUrl);
            if (!response.ok) {
              console.error("Gagal ngambil meta data file");
              newExtFiles[index] = "";
              continue;
            }
            const data = await response.json();
            let metaFilename = data?.filename || "";
            if (metaFilename) {
              const cleanMetaName = getBaseFilename(metaFilename);
              newExtFiles[index] =
                cleanMetaName.split(".").pop()?.toLowerCase() || "";
              continue;
            }
            newExtFiles[index] = "";
          } catch (e) {
            console.error(e);
            newExtFiles[index] = "";
          }
        } else {
          const ext =
            cleanName && cleanName.includes(".")
              ? cleanName.split(".").pop()?.toLowerCase() || ""
              : "";
          newExtFiles[index] = ext;
        }
      }
      setExtFiles(newExtFiles);
    };
    updateExtFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields.length,
    fields.map((_, i) => watch(`${name}.${i}.filename`)).join(","),
  ]);

  return (
    <InputWrapper
      label={label}
      {...inputWrapper}
      withAsterisk={rest.withAsterisk}
    >
      <div className="flex flex-col w-full" key={"multi-" + name}>
        {fields.map((field, index) => {
          const errIndex = (errors[name] as any)?.[index];
          const url = watch(`${name}.${index}.url`);
          const filename = watch(`${name}.${index}.filename`);
          const extFile = extFiles[index] || "";
          const cleanName = filename ? getBaseFilename(filename) : "";
          const cleanUrl = url ? sanitizeUrl(url) : "";

          return (
            <div key={field.id}>
              <div className="flex flex-grow w-full mb-2">
                <input
                  type="file"
                  onChange={(e) => handleFileChange(e.target.files?.[0], index)}
                  ref={(current) => {
                    if (current) inputRef.current[index] = current;
                  }}
                  hidden
                  accept={accept}
                />

                {filename && (
                  <>
                    <Button
                      variant="fill"
                      onClick={() =>
                        handleSelectAttachment({ url, filename, extFile })
                      }
                      color="blue"
                      className="w-full"
                      data-testid={"field-file-uploaded-" + name}
                    >
                      {cleanName || cleanUrl}
                    </Button>
                    {url && (
                      <Tooltip label={t("download", { ns: "generalMessage" })}>
                        <Button
                          variant="outline"
                          onClick={() => {
                            if (!url)
                              return console.log("File URL is not available");
                            if (!url.startsWith("blob:")) {
                              return window.open(
                                `${CURRENT_DOMAIN}/api/fetching-file?url=${encodeURIComponent(
                                  cleanUrl
                                )}`
                              );
                            }
                            return window.open(url);
                          }}
                          color="blue"
                          className="ml-2"
                          data-testid={"field-file-delete-" + name}
                        >
                          <IconDownload size={15} />
                        </Button>
                      </Tooltip>
                    )}
                  </>
                )}

                {!filename && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const current = inputRef.current[index];
                      if (current) current.click();
                    }}
                    color="blue"
                    className=" w-full"
                    disabled={readOnly}
                  >
                    {" "}
                    {t("clickHereToUpload")}
                  </Button>
                )}
                <Tooltip label={t("delete", { ns: "generalMessage" })}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      remove(index);
                    }}
                    color="red"
                    className=" ml-2"
                    disabled={readOnly}
                  >
                    {" "}
                    <IconTrash size={15} />
                  </Button>
                </Tooltip>
              </div>

              {errorFileName && <Input.Error>{errorFileName}</Input.Error>}
              {errIndex && <Input.Error>{errIndex.message}</Input.Error>}
            </div>
          );
        })}
        <Button variant="light" onClick={() => append({})} disabled={readOnly}>
          {t("addMore")}
        </Button>
      </div>
      {typeof errors[name]?.message === "string" && (
        <Input.Error>{errors[name]?.message}</Input.Error>
      )}
      <Modal
        opened={opened}
        onClose={close}
        radius={0}
        transitionProps={{ transition: "fade", duration: 200 }}
        size={"xl"}
        title={
          selectedAttachment?.filename ||
          selectedAttachment?.url ||
          "Preview File"
        }
      >
        {selectedAttachment?.filename &&
          selectedAttachment?.url &&
          selectedAttachment?.url.startsWith("blob:") ? (
          <PreviewFileUnUploaded
            url={selectedAttachment?.url}
            filename={selectedAttachment?.filename}
            extFile={selectedAttachment?.extFile}
          />
        ) : (
          <PreviewFileUploaded
            url={selectedAttachment?.url || ""}
            filename={selectedAttachment?.filename || ""}
            extFile={selectedAttachment?.extFile}
          />
        )}
      </Modal>
    </InputWrapper>
  );
};

const RESERVED_CHARS_REGEX = /[?#&=;%/\\:*"<>|+]/; // URL/header troublemakers
const CONTROL_CHARS_REGEX = /[\u0000-\u001F\u007F]/; // char kontrol
const TRAILING_BAD_REGEX = /[.\s]$/; // titik/space di akhir (Windows-bad)
const DOUBLE_DOT_REGEX = /\.\./; // path traversal-ish

const isUnsafeFilename = (name: string) => {
  // kosong / hanya spasi
  if (!name || !name.trim()) return true;
  // karakter terlarang
  if (RESERVED_CHARS_REGEX.test(name)) return true;
  // karakter kontrol
  if (CONTROL_CHARS_REGEX.test(name)) return true;
  // akhiran berbahaya
  if (TRAILING_BAD_REGEX.test(name)) return true;
  // dua titik berturut-turut
  if (DOUBLE_DOT_REGEX.test(name)) return true;
  // panjang ekstrem (opsional)
  if (name.length > 200) return true;
  return false;
};
